import { browser } from '$app/environment';
import { derived, get, writable } from 'svelte/store';
import { controllerState, getSewAndWristPoses, getHandLandmarks } from '$lib/stores/body-tracking';
import type { ControllerState } from '$lib/stores/body-tracking';
import { controlCommands, handControlCommands } from '$lib/stores/controller';
import {
	SewMimicG1Controller,
	SewMimicAHController,
	type ArmPose,
	type G1JointSolution,
	type AHJointSolution,
	type FingerPositions,
	type SewMimicInitOptions
} from '$lib/sew-mimic';
import type { vec3 } from 'gl-matrix';

// G1 Arm IK Stores
export const ikController = writable<SewMimicG1Controller | null>(null);
export const isIkLoading = writable(false);
export const isIkReady = writable(false);
export const ikError = writable<string | null>(null);
export const isG1SolverReady = writable(false);
export const latestJointSolution = writable<G1JointSolution | null>(null);
export const ikSolveRateHz = writable(0);

// Ability Hand IK Stores
export const ahController = writable<SewMimicAHController | null>(null);
export const isAHLoading = writable(false);
export const isAHReady = writable(false);
export const ahError = writable<string | null>(null);
export const latestHandSolution = writable<{ left: AHJointSolution | null; right: AHJointSolution | null }>({
	left: null,
	right: null
});
export const handIkSolveRateHz = writable(0);

// Derived Status Stores
export const isBodyTrackingActive = derived([controllerState], ([$controllerState]) => {
	if (!$controllerState) return false;

	const leftValid = Boolean($controllerState.sew_poses.left.S && $controllerState.sew_poses.left.E && $controllerState.sew_poses.left.W);
	const rightValid = Boolean($controllerState.sew_poses.right.S && $controllerState.sew_poses.right.E && $controllerState.sew_poses.right.W);

	return $controllerState.engaged && (leftValid || rightValid);
});

export const isHandTrackingActive = derived([controllerState], ([$controllerState]) => {
	if (!$controllerState || !('hand_poses' in $controllerState)) return false;
	const handPoses = $controllerState.hand_poses;
	if (!handPoses) return false;

	const leftValid = Boolean(handPoses.left?.landmarks);
	const rightValid = Boolean(handPoses.right?.landmarks);

	return leftValid || rightValid;
});

export const teleopStatus = derived(
	[isIkReady, isG1SolverReady, isAHReady, isBodyTrackingActive, isHandTrackingActive, controllerState],
	([$isIkReadyValue, $isG1SolverReadyValue, $isAHReadyValue, $isBodyTrackingActiveValue, $isHandTrackingActiveValue, $controllerState]) => ({
		runtime: $isIkReadyValue,
		solver: $isG1SolverReadyValue,
		handSolver: $isAHReadyValue,
		bodyTracking: $isBodyTrackingActiveValue,
		handTracking: $isHandTrackingActiveValue,
		canSolveIK: $isIkReadyValue && $isG1SolverReadyValue && $isBodyTrackingActiveValue,
		canSolveHandIK: $isAHReadyValue && $isHandTrackingActiveValue,
		sewPoses: $controllerState?.sew_poses ?? null,
		wristPoses: $controllerState?.wrist_poses ?? null,
		handPoses: ($controllerState && 'hand_poses' in $controllerState) ? $controllerState.hand_poses : null
	})
);

// Rate Tracking
let initializationPromise: Promise<SewMimicG1Controller> | null = null;
let ahInitializationPromise: Promise<SewMimicAHController> | null = null;
let smoothedIkHz = 0;
let smoothedHandIkHz = 0;
const IK_HZ_SMOOTHING = 0.2;
// The IK rate display reports pure solver inference speed (1000 / solve duration),
// not the teleop loop rate. When the Rust pipeline solves inline during frame
// processing, the device layer times that wasm call and reports it here.
export function recordExternalIkSolveDuration(durationMs: number) {
	recordIkSolveDuration(durationMs);
}

function recordIkSolveDuration(durationMs: number) {
	if (!browser || durationMs <= 0) {
		return;
	}
	const instantaneousHz = 1000 / durationMs;
	smoothedIkHz = smoothedIkHz * (1 - IK_HZ_SMOOTHING) + instantaneousHz * IK_HZ_SMOOTHING;
	ikSolveRateHz.set(Math.round(smoothedIkHz));
}

function recordHandIkSolveDuration(durationMs: number) {
	if (!browser || durationMs <= 0) {
		return;
	}
	const instantaneousHz = 1000 / durationMs;
	smoothedHandIkHz = smoothedHandIkHz * (1 - IK_HZ_SMOOTHING) + instantaneousHz * IK_HZ_SMOOTHING;
	handIkSolveRateHz.set(Math.round(smoothedHandIkHz));
}

// G1 Arm IK Functions
export async function initializeIkRuntime(options: SewMimicInitOptions = {}): Promise<SewMimicG1Controller> {
	if (!browser) {
		throw new Error('SEW-Mimic is only available in the browser environment');
	}

	const existing = get(ikController);
	if (existing && existing.isReady()) {
		isIkReady.set(true);
		isG1SolverReady.set(true);
		return existing;
	}

	if (!initializationPromise) {
		initializationPromise = (async () => {
			isIkLoading.set(true);
			ikError.set(null);

			try {
				const controller = new SewMimicG1Controller();
				await controller.initialize(options);
				ikController.set(controller);
				isIkReady.set(true);
				isG1SolverReady.set(true);
				return controller;
			} catch (error) {
				const message = error instanceof Error ? error.message : 'Unknown error while initializing SEW-Mimic';
				ikError.set(message);
				ikController.set(null);
				isIkReady.set(false);
				isG1SolverReady.set(false);
				throw error;
			} finally {
				isIkLoading.set(false);
			}
		})();
	}

	try {
		const controller = await initializationPromise;
		initializationPromise = null;
		return controller;
	} catch (error) {
		initializationPromise = null;
		throw error;
	}
}

export async function initializeG1Solver(options: SewMimicInitOptions = {}): Promise<void> {
	await initializeIkRuntime(options);
}

export async function solveCurrentPoses(): Promise<G1JointSolution | null> {
	const liveState = get(controllerState) as
		| (ControllerState & {
				g1_solution?: {
					left_arm: number[];
					right_arm: number[];
					torso?: number[] | null;
					control_commands: number[];
					metadata?: unknown;
				} | null;
			})
		| null;

	if (liveState?.g1_solution && liveState.engaged) {
		const directSolution: G1JointSolution = {
			left_arm: liveState.g1_solution.left_arm,
			right_arm: liveState.g1_solution.right_arm,
			torso: liveState.g1_solution.torso ?? undefined,
			control_commands: liveState.g1_solution.control_commands,
			metadata: (liveState.g1_solution.metadata as G1JointSolution['metadata']) ?? undefined
		};
		latestJointSolution.set(directSolution);
		if (directSolution.control_commands && directSolution.control_commands.length > 0) {
			controlCommands.set(directSolution.control_commands);
		} else {
			controlCommands.set(null);
		}
		return directSolution;
	}

	let controller = get(ikController);
	if (!controller || !controller.isReady()) {
		try {
			controller = await initializeIkRuntime();
		} catch (error) {
			controlCommands.set(null);
			console.error('Failed to initialize SEW-Mimic controller:', error);
			return null;
		}
	}

	if (!controller || !controller.isReady()) {
		controlCommands.set(null);
		console.error('SEW-Mimic controller not ready after initialization');
		return null;
	}

	const currentPoses = getSewAndWristPoses();
	const leftPose = toArmPose(currentPoses.sew_poses.left);
	if (leftPose && currentPoses.wrist_poses?.left) {
		leftPose.wristRotation = currentPoses.wrist_poses.left as Float32Array;
	}

	const rightPose = toArmPose(currentPoses.sew_poses.right);
	if (rightPose && currentPoses.wrist_poses?.right) {
		rightPose.wristRotation = currentPoses.wrist_poses.right as Float32Array;
	}

	const torsoAngles = currentPoses.torso_angles ? Array.from(currentPoses.torso_angles) : undefined;
	const torsoRotation = currentPoses.sew_poses.R_world_body as Float32Array | null;

	if (!leftPose && !rightPose) {
		controlCommands.set(null);
		return null;
	}

	const solveStart = browser ? performance.now() : null;
	try {
		const solution = await controller.solveIK({
			left: leftPose ?? undefined,
			right: rightPose ?? undefined,
			torso: torsoAngles,
			torsoRotation: torsoRotation ?? undefined
		});

		if (solveStart !== null && browser && typeof performance !== 'undefined') {
			recordIkSolveDuration(performance.now() - solveStart);
		}

		latestJointSolution.set(solution);
		if (solution?.control_commands && solution.control_commands.length > 0) {
			controlCommands.set(solution.control_commands);
		} else {
			controlCommands.set(null);
		}

		return solution;
	} catch (error) {
		controlCommands.set(null);
		console.error('SEW-Mimic IK solve failed:', error);
		return null;
	}
}

// Ability Hand IK Functions
export async function initializeHandIkRuntime(options: SewMimicInitOptions = {}): Promise<SewMimicAHController> {
	if (!browser) {
		throw new Error('SEW-Mimic is only available in the browser environment');
	}

	const existing = get(ahController);
	if (existing && existing.isReady()) {
		isAHReady.set(true);
		return existing;
	}

	if (!ahInitializationPromise) {
		ahInitializationPromise = (async () => {
			isAHLoading.set(true);
			ahError.set(null);

			try {
				const controller = new SewMimicAHController();
				await controller.initialize(options);
				ahController.set(controller);
				isAHReady.set(true);
				return controller;
			} catch (error) {
				const message = error instanceof Error ? error.message : 'Unknown error while initializing Hand IK';
				ahError.set(message);
				ahController.set(null);
				isAHReady.set(false);
				throw error;
			} finally {
				isAHLoading.set(false);
			}
		})();
	}

	try {
		const controller = await ahInitializationPromise;
		ahInitializationPromise = null;
		return controller;
	} catch (error) {
		ahInitializationPromise = null;
		throw error;
	}
}

// Low-pass filter for hand joint commands: filtered = prev + alpha * (raw - prev).
// Lives here (not in the WASM solver) because the Rust ah_ik_from_positions creates a
// fresh FilterState on every call, so its filter_alpha has no effect across frames.
const HAND_FILTER_ALPHA = 0.3;
const handCommandFilterState: { left: number[] | null; right: number[] | null } = {
	left: null,
	right: null
};

function filterHandCommands(side: 'left' | 'right', raw: number[]): number[] {
	const prev = handCommandFilterState[side];
	if (!prev || prev.length !== raw.length) {
		handCommandFilterState[side] = raw.slice();
		return raw;
	}
	const filtered = raw.map((value, i) => prev[i] + HAND_FILTER_ALPHA * (value - prev[i]));
	handCommandFilterState[side] = filtered;
	return filtered;
}

export async function solveCurrentHandPoses(): Promise<{ left: AHJointSolution | null; right: AHJointSolution | null }> {
	let controller = get(ahController);
	if (!controller || !controller.isReady()) {
		try {
			controller = await initializeHandIkRuntime();
		} catch (error) {
			handControlCommands.set({ left: null, right: null });
			console.error('Failed to initialize Hand IK controller:', error);
			return { left: null, right: null };
		}
	}

	if (!controller || !controller.isReady()) {
		handControlCommands.set({ left: null, right: null });
		console.error('Hand IK controller not ready after initialization');
		return { left: null, right: null };
	}

	const liveState = get(controllerState) as
		| (ControllerState & {
				finger_positions_wrist_frame?: {
					left?: Record<string, { mcp?: number[]; pip?: number[]; tip?: number[] }> | null;
					right?: Record<string, { mcp?: number[]; pip?: number[]; tip?: number[] }> | null;
				};
		  })
		| null;

	const wristFrameFingers = liveState?.finger_positions_wrist_frame;
	const handLandmarks = getHandLandmarks();
	const result: { left: AHJointSolution | null; right: AHJointSolution | null } = {
		left: null,
		right: null
	};
	const commands: { left: number[] | null; right: number[] | null } = {
		left: null,
		right: null
	};

	const solveStart = browser ? performance.now() : null;

	const leftFingers =
		normalizeWristFrameFingerPositions(wristFrameFingers?.left) ??
		(handLandmarks.left ? convertHandLandmarksToFingerPositions(handLandmarks.left) : null);
	const rightFingers =
		normalizeWristFrameFingerPositions(wristFrameFingers?.right) ??
		(handLandmarks.right ? convertHandLandmarksToFingerPositions(handLandmarks.right) : null);

	if (leftFingers) {
		try {
			const leftSolution = await controller.solveIK({ fingers: leftFingers });
			result.left = leftSolution;
			const rawLeft = mapHandSolutionToG1ActuatorOrder(leftSolution);
			commands.left = rawLeft ? filterHandCommands('left', rawLeft) : null;
		} catch (error) {
			console.warn('Left hand IK solve failed:', error);
		}
	} else {
		handCommandFilterState.left = null;
	}

	if (rightFingers) {
		try {
			const rightSolution = await controller.solveIK({ fingers: rightFingers });
			result.right = rightSolution;
			const rawRight = mapHandSolutionToG1ActuatorOrder(rightSolution);
			commands.right = rawRight ? filterHandCommands('right', rawRight) : null;
		} catch (error) {
			console.warn('Right hand IK solve failed:', error);
		}
	} else {
		handCommandFilterState.right = null;
	}

	if (solveStart !== null && browser && typeof performance !== 'undefined') {
		recordHandIkSolveDuration(performance.now() - solveStart);
	}

	latestHandSolution.set(result);
	handControlCommands.set(commands);

	return result;
}

// Combined Solve Function
export async function solveAllPoses(): Promise<{
	arm: G1JointSolution | null;
	hands: { left: AHJointSolution | null; right: AHJointSolution | null };
}> {
	const [armSolution, handSolutions] = await Promise.all([
		solveCurrentPoses(),
		solveCurrentHandPoses()
	]);

	return {
		arm: armSolution,
		hands: handSolutions
	};
}

// Cleanup
export function destroyIkRuntime(): void {
	const controller = get(ikController);
	if (controller) {
		controller.destroy();
	}

	ikController.set(null);
	isIkLoading.set(false);
	isIkReady.set(false);
	isG1SolverReady.set(false);
	ikError.set(null);
	latestJointSolution.set(null);
	initializationPromise = null;
}

export function destroyHandIkRuntime(): void {
	const controller = get(ahController);
	if (controller) {
		controller.destroy();
	}

	ahController.set(null);
	isAHLoading.set(false);
	isAHReady.set(false);
	ahError.set(null);
	latestHandSolution.set({ left: null, right: null });
	ahInitializationPromise = null;
}

export function destroyAllIkRuntimes(): void {
	destroyIkRuntime();
	destroyHandIkRuntime();
}

// Conversion Helpers
function toArmPose(value: unknown): ArmPose | null {
	if (!value || typeof value !== 'object') {
		return null;
	}

	const record = value as Record<string, unknown>;
	const shoulder = normalizePoseVector(record.S ?? record.shoulder);
	const elbow = normalizePoseVector(record.E ?? record.elbow);
	const wrist = normalizePoseVector(record.W ?? record.wrist);
	const wristRotation = record.R_T ?? record.R ?? record.wristRotation ?? record.rotation;

	if (!shoulder || !elbow || !wrist) {
		return null;
	}

	return {
		shoulder,
		elbow,
		wrist,
		wristRotation: wristRotation as ArmPose['wristRotation']
	};
}

function normalizePoseVector(value: unknown): ArmPose['shoulder'] | null {
	if (!value) {
		return null;
	}

	if (value instanceof Float32Array || value instanceof Float64Array) {
		return value;
	}

	if (Array.isArray(value)) {
		return value as number[];
	}

	if (typeof value === 'object') {
		const record = value as Record<string, unknown>;
		const xyz = ['x', 'y', 'z'];
		if (xyz.every((key) => typeof record[key] === 'number')) {
			return xyz.map((key) => Number(record[key]));
		}
		const numericValues = Object.values(record).filter((entry): entry is number => typeof entry === 'number');
		if (numericValues.length >= 3) {
			return numericValues.slice(0, 3).map(Number);
		}
	}

	return null;
}

/**
 * Converts MediaPipe hand landmarks to the format expected by the Ability Hand IK solver.
 *
 * MediaPipe landmarks use keys like: 'thumb_tip', 'index_finger_mcp', etc.
 * AH IK expects: { thumb: { mcp, pip, tip }, index: { mcp, pip, tip }, ... }
 */
function convertHandLandmarksToFingerPositions(
	landmarks: Record<string, vec3>
): FingerPositions | null {
	// Helper to extract vec3 as number array
	const toArray = (v: vec3 | undefined): number[] | undefined => {
		if (!v) return undefined;
		return [v[0], v[1], v[2]];
	};

	// Map MediaPipe landmark names to our finger structure
	// MediaPipe uses: thumb_mcp, thumb_ip (for PIP), thumb_tip
	// For other fingers: index_finger_mcp, index_finger_pip, index_finger_dip, index_finger_tip
	// Note: We use PIP for the middle joint as that's what the IK expects

	const fingers: FingerPositions = {};

	// Thumb - MediaPipe uses thumb_mcp, thumb_ip, thumb_tip
	const thumbMcp = toArray(landmarks['thumb_mcp']);
	const thumbPip = toArray(landmarks['thumb_ip']); // thumb uses IP instead of PIP
	const thumbTip = toArray(landmarks['thumb_tip']);
	if (thumbMcp && thumbPip && thumbTip) {
		fingers.thumb = { mcp: thumbMcp, pip: thumbPip, tip: thumbTip };
	}

	// Index finger
	const indexMcp = toArray(landmarks['index_finger_mcp']);
	const indexPip = toArray(landmarks['index_finger_pip']);
	const indexTip = toArray(landmarks['index_finger_tip']);
	if (indexMcp && indexPip && indexTip) {
		fingers.index = { mcp: indexMcp, pip: indexPip, tip: indexTip };
	}

	// Middle finger
	const middleMcp = toArray(landmarks['middle_finger_mcp']);
	const middlePip = toArray(landmarks['middle_finger_pip']);
	const middleTip = toArray(landmarks['middle_finger_tip']);
	if (middleMcp && middlePip && middleTip) {
		fingers.middle = { mcp: middleMcp, pip: middlePip, tip: middleTip };
	}

	// Ring finger
	const ringMcp = toArray(landmarks['ring_finger_mcp']);
	const ringPip = toArray(landmarks['ring_finger_pip']);
	const ringTip = toArray(landmarks['ring_finger_tip']);
	if (ringMcp && ringPip && ringTip) {
		fingers.ring = { mcp: ringMcp, pip: ringPip, tip: ringTip };
	}

	// Pinky finger
	const pinkyMcp = toArray(landmarks['pinky_mcp']);
	const pinkyPip = toArray(landmarks['pinky_pip']);
	const pinkyTip = toArray(landmarks['pinky_tip']);
	if (pinkyMcp && pinkyPip && pinkyTip) {
		fingers.pinky = { mcp: pinkyMcp, pip: pinkyPip, tip: pinkyTip };
	}

	// Need at least index and pinky for palm frame computation
	if (!fingers.index || !fingers.pinky) {
		return null;
	}

	return fingers;
}

function normalizeWristFrameFingerPositions(
	raw:
		| Record<string, { mcp?: number[]; pip?: number[]; tip?: number[] }>
		| null
		| undefined
): FingerPositions | null {
	if (!raw) {
		return null;
	}

	const toVec = (value?: number[]): number[] | undefined => {
		if (!Array.isArray(value) || value.length < 3) {
			return undefined;
		}
		return [Number(value[0]), Number(value[1]), Number(value[2])];
	};

	const fingerNames = ['thumb', 'index', 'middle', 'ring', 'pinky'] as const;
	const fingers: FingerPositions = {};

	for (const finger of fingerNames) {
		const source = raw[finger];
		if (!source) {
			continue;
		}

		const mcp = toVec(source.mcp);
		const pip = toVec(source.pip);
		const tip = toVec(source.tip);
		if (mcp && pip && tip) {
			fingers[finger] = { mcp, pip, tip };
		}
	}

	if (!fingers.index || !fingers.pinky) {
		return null;
	}

	return fingers;
}

function mapHandSolutionToG1ActuatorOrder(solution: AHJointSolution): number[] | null {
	const fingers = solution.fingers;
	if (!fingers) {
		return null;
	}

	const pair = (angles?: [number, number]): [number, number] => {
		if (!angles) {
			return [0, 0];
		}
		return [Number(angles[0]), Number(angles[1])];
	};

	const [indexMcp, indexPip] = pair(fingers.index);
	const [middleMcp, middlePip] = pair(fingers.middle);
	const [ringMcp, ringPip] = pair(fingers.ring);
	const [pinkyMcp, pinkyPip] = pair(fingers.pinky);
	const q1Scale = 1.7;

	// Ability Hand thumb group is [thumb_cmc, thumb_mcp],
	// while g1_with_hands actuators expect [thumb_mcp, thumb_cmc].
	const [thumbCmc, thumbMcp] = pair(fingers.thumb);

	const mapped = [
		indexMcp * q1Scale,
		indexPip,
		middleMcp * q1Scale,
		middlePip,
		ringMcp * q1Scale,
		ringPip,
		pinkyMcp * q1Scale,
		pinkyPip,
		thumbMcp,
		thumbCmc
	];
	return mapped;
}
